// src/components/documents/EstimateSendPage.js — doced-8f3a61c2 (Document editor, Stage 5f: Review & send)
//
// /estimates/:ref/send — its own page, not a pop-up. Left: To (the estimate's own address — the server never
// takes another, founder decision 2026-10-27; Stage 5f-2's inline "Change" edits the SAVED estimate's copy,
// and the customer record if ticked), CC, Subject, Message, "Attach the estimate as a
// PDF" (unticked), Back and Send. Right: what the customer gets — the email exactly as the server builds it
// (previewQuoteEmail, the same builder sendQuoteEmail uses) and the PDF (getQuotePdf, the same PDF that is
// attached). Shown for the SAVED estimate: the editor saves before it opens this page.
//
// Send = today's Save & Send (see utils/estimateSend.js): the send stamp merged into the estimate inside the
// conflict-checked transaction, then sendQuoteEmail with cc / subject / message / attachPdf. Nothing else.
// Viewed stays "the customer opened the estimate in the portal" — no open-tracking anywhere.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { db, functions } from '../../firebase/config';
import { doc, getDoc, updateDoc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Loader2, AlertCircle, CheckCircle2, Mail, FileText, ExternalLink, ChevronLeft } from 'lucide-react';
import useUnsavedGuard from '../../hooks/useUnsavedGuard';
import { FIELD } from './fieldStyles';
import * as lineIds from '../../utils/lineIds';
import { parseLocalDate, formatLocalDate } from '../../utils/formatters';
import { generateItemId, resolveDocTerminology, resolveQuoteValidityDays, normalizeStatus, QUOTE_STATUSES } from '../../utils/quoteSchema';
import { newEstimateLine } from '../../utils/catalogPickLines';
import { fingerprintOf, saveWithConflictCheck, EstimateConflictError } from '../../utils/estimateConflict';
import {
    sendBlockedReason, recipientOf, parseCcInput, subjectProblem, messageProblem, sendRequest, previewRequest,
    buildSendStamp, replyLine, MAX_SUBJECT, MAX_MESSAGE,
} from '../../utils/estimateSend';
import { pdfPreviewUrl } from '../../utils/pdfPreviewUrl';
import CustomerEmailChange from './CustomerEmailChange'; // doced-8f3a61c2 — Stage 5f-2
import { changeEstimateEmail, canUpdateCustomerRecord } from '../../utils/estimateEmailChange';

const LEAVE_MESSAGE = 'Your email isn’t sent yet. Leave without sending?';
const PREVIEW_DELAY_MS = 600;
const IDS = { generateItemId, newLine: lineIds.newLine, newLineId: lineIds.newLineId };

function freshDays(companySettings) {
    const today = formatLocalDate();
    const exp = new Date();
    exp.setDate(exp.getDate() + resolveQuoteValidityDays(companySettings?.quoteDefaults));
    return { today, defaultExpiry: formatLocalDate(exp) };
}
const callableMessage = (e, fallback) => (e && e.message ? e.message.replace(/^Failed to send email: /, '') : fallback);

export default function EstimateSendPage({
    orgId, quote, companySettings, currentUserProfile, user, listLabel, customers,
    onBack, onBackToList, onDone,
}) {
    const term = resolveDocTerminology(quote?.docType ?? companySettings?.quoteTerminologyDefault);
    const docWord = term.word;
    const docWordLower = docWord.toLowerCase();
    const number = quote?.quoteNumber;
    const label = number ? `${docWord} ${number}` : docWord;
    const to = recipientOf(quote);
    const blocked = sendBlockedReason(quote, docWordLower);
    const wasSent = normalizeStatus(quote?.status) === QUOTE_STATUSES.SENT;

    // ── the form ─────────────────────────────────────────────────────────────
    const [ccText, setCcText] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [attachPdf, setAttachPdf] = useState(false);
    const [touched, setTouched] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const cc = useMemo(() => parseCcInput(ccText), [ccText]);
    const problems = {
        cc: cc.problem,
        subject: subjectProblem(subject),
        message: messageProblem(message),
    };

    // ── the email preview (the server builds it) ─────────────────────────────
    const [defaults, setDefaults] = useState(null); // { subject, message } once loaded
    const [preview, setPreview] = useState({ html: '', replyToEmail: '', replyToName: '', cc: [], loading: true, error: '' });
    const previewSeq = useRef(0);
    const runPreview = async (req, { first = false } = {}) => {
        const seq = ++previewSeq.current;
        setPreview((p) => ({ ...p, loading: true, error: '' }));
        try {
            const res = await httpsCallable(functions, 'previewQuoteEmail')(req);
            if (seq !== previewSeq.current) return;
            const d = res.data || {};
            if (first) { setDefaults({ subject: d.defaultSubject, message: d.defaultMessage }); setSubject(d.defaultSubject || ''); setMessage(d.defaultMessage || ''); }
            setPreview({ html: d.html || '', replyToEmail: d.replyToEmail || '', replyToName: d.replyToName || '', cc: d.cc || [], loading: false, error: '' });
        } catch (e) {
            if (seq !== previewSeq.current) return;
            console.error('doced: email preview failed', e);
            setPreview((p) => ({ ...p, loading: false, error: callableMessage(e, 'The email preview couldn’t be made. Check your connection and try again.') }));
        }
    };
    const quoteId = quote?.id;
    useEffect(() => { if (quoteId && !blocked) runPreview({ quoteId }, { first: true }); }, [quoteId]); // eslint-disable-line react-hooks/exhaustive-deps
    // After typing pauses, and whenever the estimate itself changes (someone saved it elsewhere).
    const liveKey = quote ? JSON.stringify(fingerprintOf(quote)) : '';
    useEffect(() => {
        if (!defaults || !quoteId) return undefined;
        const t = setTimeout(() => runPreview(previewRequest({ quoteId, cc: cc.problem ? [] : cc.list, subject, message })), PREVIEW_DELAY_MS);
        return () => clearTimeout(t);
    }, [defaults, quoteId, ccText, subject, message, liveKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── the PDF tab (the same PDF that is attached) ──────────────────────────
    const [tab, setTab] = useState('email');
    const [pdf, setPdf] = useState({ url: '', fileName: '', loading: false, error: '', forKey: '' });
    const pdfUrlRef = useRef('');
    useEffect(() => () => { if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current); }, []);
    const loadPdf = async () => {
        setPdf((p) => ({ ...p, loading: true, error: '' }));
        try {
            const res = await httpsCallable(functions, 'getQuotePdf', { timeout: 120000 })({ quoteId });
            const url = pdfPreviewUrl(res.data && res.data.pdfBase64);
            if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = url;
            setPdf({ url, fileName: (res.data && res.data.fileName) || `${docWord}.pdf`, loading: false, error: '', forKey: liveKey });
        } catch (e) {
            console.error('doced: PDF preview failed', e);
            setPdf((p) => ({ ...p, loading: false, error: callableMessage(e, 'The PDF couldn’t be made. Please try again.') }));
        }
    };
    useEffect(() => {
        if (tab === 'pdf' && quoteId && !pdf.loading && pdf.forKey !== liveKey) loadPdf();
    }, [tab, quoteId, liveKey]); // eslint-disable-line react-hooks/exhaustive-deps
    // A phone shows only the first page of a PDF inside a frame: there, the PDF opens in its own tab instead.
    const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(max-width: 1023px)').matches);
    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mq = window.matchMedia('(max-width: 1023px)');
        const on = () => setNarrow(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', on); else if (mq.addListener) mq.addListener(on);
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else if (mq.removeListener) mq.removeListener(on); };
    }, []);

    // ── change the address (doced-8f3a61c2 — Stage 5f-2) ─────────────────────
    // The saved estimate's copy first (conflict-checked), then the customer record if ticked. The page's
    // live estimate then shows the new To, and the preview refreshes because the estimate changed.
    const [changingEmail, setChangingEmail] = useState(false);
    const [emailNotice, setEmailNotice] = useState('');
    const customerRecord = (customers || []).find((c) => c.id === quote?.customerId) || null;
    const saveNewAddress = async (email, alsoRecord) => {
        const r = await changeEstimateEmail({ stored: quote, email, alsoRecord, recordEmail: customerRecord?.email }, {
            runTransaction, db, updateDoc, serverTimestamp,
            quoteRef: doc(db, `organizations/${orgId}/quotes`, quoteId),
            customerRef: customerRecord ? doc(db, `organizations/${orgId}/customers`, customerRecord.id) : null,
        });
        if (!r.ok) return r;
        setChangingEmail(false);
        setEmailNotice(r.record === 'refused' || r.estimate === 'unchanged' ? r.message : ''); // refused, or "Customer record updated."
        return r;
    };

    // ── send ─────────────────────────────────────────────────────────────────
    const [phase, setPhase] = useState('form'); // form | sending | sent
    const [sendError, setSendError] = useState('');
    const [result, setResult] = useState(null);
    const guard = useUnsavedGuard({ when: touched && phase !== 'sent', message: LEAVE_MESSAGE });

    const handleSend = async () => {
        if (phase === 'sending') return;
        setShowErrors(true); setSendError('');
        if (blocked || !to || problems.cc || problems.subject || problems.message) return;
        setPhase('sending');
        const { today, defaultExpiry } = freshDays(companySettings);
        try {
            // 1. The send stamp — what Save & Send writes on top of a save — refused if the estimate changed.
            const ref = doc(db, `organizations/${orgId}/quotes`, quoteId);
            const stamp = buildSendStamp(quote, {
                serverTimestamp, parseLocalDate, formatLocalDate, ensureLineIds: lineIds.ensureLineIds, newLineId: lineIds.newLineId, today, defaultExpiry, companySettings,
                newLine: () => newEstimateLine(IDS), generateItemId,
            });
            await saveWithConflictCheck({ runTransaction, db, ref, write: stamp, isNew: false, baseline: fingerprintOf(quote) });
        } catch (e) {
            setPhase('form');
            if (e instanceof EstimateConflictError) setSendError(`${e.message.replace(/Nothing was saved\./, 'Nothing was sent.')}`);
            else if (e && e.code === 'permission-denied') setSendError(`Spatchy didn’t allow this. You may not have permission to send this ${docWordLower}.`);
            else { console.error('doced: send stamp failed', e); setSendError(`The ${docWordLower} couldn’t be marked Sent, so nothing was sent. Check your connection and try again.`); }
            return;
        }
        try {
            // 2. The email — the same callable as the pop-up's Save & Send and Resend.
            const req = sendRequest({ quoteId, cc: cc.list, subject, message, attachPdf });
            await httpsCallable(functions, 'sendQuoteEmail', attachPdf ? { timeout: 120000 } : undefined)(req);
            let fresh = quote;
            try { const s = await getDoc(doc(db, `organizations/${orgId}/quotes`, quoteId)); if (s.exists()) fresh = { id: s.id, ...s.data() }; } catch (_) { /* the number we have is fine */ }
            setResult({ number: fresh.quoteNumber || number, to, cc: preview.cc.length ? preview.cc : cc.list, attachPdf });
            setPhase('sent');
        } catch (e) {
            console.error('doced: send email failed', e);
            setPhase('form');
            setSendError(`The ${docWordLower} is now marked Sent, but the email didn’t go out: ${callableMessage(e, 'unknown error')}. You can press Send again.`);
        }
    };

    // ── screens ──────────────────────────────────────────────────────────────
    const leave = (fn) => { if (!guard.confirm()) return; guard.allow(fn); };
    const card = 'bg-white border border-gray-200 rounded-2xl p-5';
    const fieldLabel = 'text-[13px] font-semibold text-gray-700';
    const err = (p) => (showErrors && p ? <span role="alert" className="text-xs text-red-600 font-semibold">{p}</span> : null);

    if (phase === 'sent' && result) {
        return (
            <div className="bg-[#f5f6f8] min-h-full">
                <div className="max-w-xl mx-auto px-4 py-12 flex flex-col items-center text-center gap-4">
                    <CheckCircle2 size={44} className="text-green-600" aria-hidden="true" />
                    <h1 className="text-2xl font-bold text-gray-900">{result.number ? `${docWord} ${result.number} sent` : `${docWord} sent`}</h1>
                    <div className="text-sm text-gray-700 flex flex-col gap-1">
                        <p>To <strong>{result.to}</strong></p>
                        {result.cc.length > 0 && <p>CC {result.cc.join(', ')}</p>}
                        {result.attachPdf && <p>With the PDF attached</p>}
                    </div>
                    <button type="button" onClick={() => guard.allow(onDone)} className="min-h-[46px] px-6 rounded-lg bg-[#5F4EC9] text-white font-bold hover:bg-[#4f40b0]">
                        Back to the {docWordLower}
                    </button>
                </div>
            </div>
        );
    }

    const replyText = replyLine(preview, currentUserProfile?.email || user?.email);
    const sendDisabled = phase === 'sending' || !!blocked || !to;

    return (
        <div className="bg-[#f5f6f8] min-h-full">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-5 pt-5 pb-16 flex flex-col gap-4">
                <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-gray-500">
                    <button type="button" onClick={() => leave(onBackToList)} className="min-h-[44px] hover:text-gray-800">{listLabel}</button>
                    <span aria-hidden="true">/</span>
                    <button type="button" onClick={() => leave(onBack)} className="min-h-[44px] hover:text-gray-800">{number || docWord}</button>
                    <span aria-hidden="true">/</span>
                    <span className="text-gray-900 font-semibold">Review &amp; send</span>
                </nav>
                <h1 className="text-2xl font-bold text-gray-900">Review &amp; send {label}</h1>

                {blocked && (
                    <div role="alert" className="p-3 rounded-lg bg-amber-50 border border-amber-300 text-sm text-amber-900 flex items-start gap-2">
                        <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden="true" /><span>{blocked}</span>
                    </div>
                )}

                <div className="grid gap-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start">
                    {/* ── left: the email ── */}
                    <section aria-label="The email" className={`${card} flex flex-col gap-4 min-w-0`}>
                        <div className="flex flex-col gap-1.5">
                            <span className={fieldLabel}>To</span>
                            {/* doced-8f3a61c2 — Stage 5f-2: the address is changed HERE (inline), on the saved estimate —
                                the send still reads To only from the saved estimate. */}
                            {to ? (
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <span className="text-[15px] text-gray-900 break-all">{to}</span>
                                    {!changingEmail && !blocked && <button type="button" onClick={() => { setChangingEmail(true); setEmailNotice(''); }} className="min-h-[44px] text-sm font-semibold text-[#5F4EC9] hover:underline">Change</button>}
                                </div>
                            ) : (
                                <div className="flex flex-col gap-1">
                                    <span role="alert" className="text-sm text-red-700">This {docWordLower} has no customer email address, so it can’t be sent.</span>
                                    {!changingEmail && !blocked && <button type="button" onClick={() => { setChangingEmail(true); setEmailNotice(''); }} className="self-start min-h-[44px] text-sm font-semibold text-[#5F4EC9] hover:underline">Add an email address</button>}
                                </div>
                            )}
                            {changingEmail && (
                                <CustomerEmailChange
                                    idPrefix="send-to"
                                    current={to}
                                    recordEmail={customerRecord?.email}
                                    customerName={quote?.customerName || quote?.customerDetails?.name}
                                    canUpdateRecord={canUpdateCustomerRecord(currentUserProfile, companySettings)}
                                    hasRecord={!!customerRecord}
                                    onSave={saveNewAddress}
                                    onCancel={() => setChangingEmail(false)}
                                />
                            )}
                            {emailNotice && <p role="status" className="text-sm text-amber-800">{emailNotice}</p>}
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="send-cc" className={fieldLabel}>CC <span className="font-normal text-gray-400">(optional)</span></label>
                            <input id="send-cc" type="text" inputMode="email" autoComplete="off" value={ccText}
                                onChange={(e) => { setCcText(e.target.value); setTouched(true); }}
                                placeholder="name@example.com, another@example.com"
                                className={`${FIELD} ${cc.problem && (showErrors || ccText.trim()) ? '!border-red-400' : ''}`} />
                            {cc.problem && (showErrors || ccText.trim()) ? <span role="alert" className="text-xs text-red-600 font-semibold">{cc.problem}</span>
                                : <span className="text-xs text-gray-500">Separate addresses with commas. Up to 5.</span>}
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="send-subject" className={fieldLabel}>Subject</label>
                            <input id="send-subject" type="text" value={subject} maxLength={MAX_SUBJECT + 50}
                                onChange={(e) => { setSubject(e.target.value); setTouched(true); }}
                                className={FIELD} disabled={!defaults} />
                            {err(problems.subject)}
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="send-message" className={fieldLabel}>Message</label>
                            <textarea id="send-message" rows={8} value={message}
                                onChange={(e) => { setMessage(e.target.value); setTouched(true); }}
                                disabled={!defaults}
                                className="w-full px-3 py-2.5 text-[15px] border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/40" />
                            {err(problems.message)}
                            {message.length > MAX_MESSAGE - 500 && <span className="text-xs text-gray-500 text-right">{message.length.toLocaleString('en-US')} / {MAX_MESSAGE.toLocaleString('en-US')}</span>}
                            {message.includes('{portalLink}') && <span className="text-xs text-gray-500">{'{portalLink}'} becomes the link to the {docWordLower} when the email is sent.</span>}
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
                                <input type="checkbox" checked={attachPdf} onChange={(e) => { setAttachPdf(e.target.checked); setTouched(true); }}
                                    className="mt-1 h-5 w-5 rounded border-gray-300 text-[#5F4EC9] focus:ring-[#5F4EC9]" />
                                <span className="text-[15px] text-gray-800">Attach the {docWordLower} as a PDF</span>
                            </label>
                            {attachPdf && (
                                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                                    With the PDF attached, you may not see when they read it. You’ll only see Viewed if they click “View &amp; Approve {docWord} Online”.
                                </p>
                            )}
                        </div>
                        <p className="text-sm text-gray-600">
                            {wasSent ? `Sending again keeps this ${docWordLower} Sent and restarts its follow-up reminders.` : `Sending marks this ${docWordLower} Sent.`}
                            {replyText ? ` ${replyText}` : ''}
                        </p>
                        {sendError && (
                            <div role="alert" className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                                <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden="true" /><span>{sendError}</span>
                            </div>
                        )}
                        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-1">
                            <button type="button" onClick={() => leave(onBack)} className="min-h-[46px] px-5 rounded-lg border border-gray-300 bg-white font-semibold text-gray-700 hover:bg-gray-50 inline-flex items-center justify-center gap-1">
                                <ChevronLeft size={16} aria-hidden="true" />Back
                            </button>
                            <button type="button" onClick={handleSend} disabled={sendDisabled}
                                className="min-h-[46px] px-6 rounded-lg bg-[#5F4EC9] text-[15px] font-bold text-white hover:bg-[#4f40b0] disabled:opacity-60 inline-flex items-center justify-center gap-2">
                                {phase === 'sending' && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
                                {phase === 'sending' ? 'Sending…' : `Send ${docWordLower}`}
                            </button>
                        </div>
                    </section>

                    {/* ── right: what the customer gets ── */}
                    <section aria-label="What the customer gets" className={`${card} flex flex-col gap-3 min-w-0`}>
                        <div role="tablist" aria-label="Preview" className="flex gap-1 border-b border-gray-200">
                            {[['email', 'Email', Mail], ['pdf', 'PDF', FileText]].map(([key, word, Icon]) => (
                                <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                                    className={`min-h-[44px] px-4 -mb-px border-b-2 text-sm font-semibold inline-flex items-center gap-2 ${tab === key ? 'border-[#5F4EC9] text-[#5F4EC9]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                                    <Icon size={15} aria-hidden="true" />{word}
                                </button>
                            ))}
                            {tab === 'email' && preview.loading && <span className="ml-auto self-center text-xs text-gray-400 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" aria-hidden="true" />Updating…</span>}
                        </div>
                        {tab === 'email' && (
                            <div role="tabpanel" aria-label="Email preview" className="flex flex-col gap-2">
                                <div className="text-sm text-gray-700 flex flex-col gap-0.5 break-words">
                                    <span><span className="text-gray-500">Subject:</span> {subjectProblem(subject) ? (defaults?.subject || '') : subject.trim()}</span>
                                    {preview.cc.length > 0 && <span><span className="text-gray-500">CC:</span> {preview.cc.join(', ')}</span>}
                                    {attachPdf && <span className="text-gray-500">Attachment: {pdf.fileName || `${docWord}${number ? `-${number}` : ''}.pdf`}</span>}
                                </div>
                                {preview.error ? (
                                    <div role="alert" className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{preview.error}</div>
                                ) : preview.html ? (
                                    <iframe title="Email preview" sandbox="" srcDoc={preview.html} className="w-full h-[560px] lg:h-[680px] border border-gray-200 rounded-lg bg-white" />
                                ) : (
                                    <div className="h-40 flex items-center justify-center text-sm text-gray-400"><Loader2 className="animate-spin" aria-hidden="true" /></div>
                                )}
                                <p className="text-xs text-gray-500">The button in the email opens this {docWordLower} in the customer’s portal.</p>
                            </div>
                        )}
                        {tab === 'pdf' && (
                            <div role="tabpanel" aria-label="PDF preview" className="flex flex-col gap-2">
                                {pdf.loading && <div className="h-40 flex items-center justify-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" aria-hidden="true" />Making the PDF…</div>}
                                {pdf.error && (
                                    <div role="alert" className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex flex-col gap-2">
                                        <span>{pdf.error}</span>
                                        <button type="button" onClick={loadPdf} className="self-start min-h-[44px] px-3 rounded-lg border border-red-300 bg-white font-semibold">Try again</button>
                                    </div>
                                )}
                                {!pdf.loading && pdf.url && (narrow ? (
                                    <button type="button" onClick={() => window.open(pdf.url, '_blank', 'noopener')}
                                        className="min-h-[46px] rounded-lg border border-gray-300 bg-white font-semibold text-gray-800 inline-flex items-center justify-center gap-2">
                                        <ExternalLink size={16} aria-hidden="true" />Open the PDF
                                    </button>
                                ) : (
                                    <iframe title="PDF preview" src={pdf.url} className="w-full h-[680px] border border-gray-200 rounded-lg bg-white" />
                                ))}
                                <p className="text-xs text-gray-500">This is the PDF on file{attachPdf ? ' — the same file is attached to the email' : ''}. The customer can also download it from the portal.</p>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}
